import { useState } from "react";
import { ApiError, post } from "../api";
import { label } from "../components/Money";
import { Loading, Refused } from "../components/Shell";
import { useApi } from "../useApi";
import { Scrolls } from "../components/Table";

interface Item {
  id: string; sent_date: string | null; sender: string; subject: string;
  category: string; confidence: number; linked_event: string | null;
  link_confirmation: string; confirmed_by: string | null;
}
interface Payload { items: Item[]; events: string[]; may_confirm: boolean }

export function Correspondence() {
  const { data, error, loading, reload } = useApi<Payload>("/correspondence");
  const [focus, setFocus] = useState<string | null>(null);
  const [target, setTarget] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;

  // The first row is what the list leads with; selecting the last item put
  // whatever was ingested most recently -- once, a junk upload -- in the
  // panel before anyone had clicked anything.
  const selected = data.items.find((i) => i.id === focus) ?? data.items[0];

  async function decide(decision: "confirm" | "reject", reference?: string) {
    if (!selected) return;
    setBusy(true); setProblem(null);
    try {
      await post(`/correspondence/${selected.id}/link`,
        { decision, event_reference: reference ?? "" });
      reload();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.detail : String(e));
    } finally { setBusy(false); }
  }

  return (
    <>
      <h1>Correspondence</h1>
      <p className="standfirst">
        {data.items.length} letters read from the project mailbox, classified and
        proposed against events. Neev proposes the link; a person confirms it
        before it counts as evidence.
      </p>

      <div className="split">
        <div>
          <Scrolls>
            <table>
              <thead><tr><th>Date</th><th>Class</th><th>Subject</th><th>Event</th><th className="num">Conf</th></tr></thead>
              <tbody>
                {data.items.map((i) => (
                  <tr key={i.id} className={i.id === selected?.id ? "on" : ""}
                      onClick={() => setFocus(i.id)} style={{ cursor: "pointer" }}>
                    <td className="mn">{i.sent_date}</td>
                    <td><span className="pill">{label(i.category)}</span></td>
                    <td title={i.subject} style={{ maxWidth: "34ch", overflow: "hidden",
                         textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.subject}</td>
                    <td className="mn">{i.linked_event ?? <span className="withheld">—</span>}</td>
                    <td className="num mn">{i.confidence.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Scrolls>
        </div>

        <aside>
          {!selected ? (
            <section className="card"><p className="empty">Select a letter to see its proposed link.</p></section>
          ) : (
            <section className="card">
              <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                <span className="pill">{label(selected.category)}</span>
                <span className="mn withheld">{selected.sent_date}</span>
              </div>
              <h2 className="ser" style={{ fontSize: 18, fontWeight: 500, margin: "0 0 12px" }}>
                {selected.subject}
              </h2>
              <div className="k">Proposed link</div>
              <div className="mn" style={{ fontSize: 18, fontWeight: 600 }}>
                {selected.linked_event ?? "none"}
              </div>

              {selected.link_confirmation === "confirmed" && (
                <div className="note">
                  <span className="h">Confirmed by {selected.confirmed_by ?? "—"}</span>
                  This letter now counts as evidence against {selected.linked_event}.
                </div>
              )}
              {selected.link_confirmation === "rejected" && (
                <div className="note">
                  <span className="h">Proposal rejected by {selected.confirmed_by ?? "—"}</span>
                  This letter is linked to nothing.
                </div>
              )}
              {selected.link_confirmation === "unconfirmed" && (
                <div className="note">
                  <span className="h">Nothing here is evidence yet</span>
                  Classification is the one place a language model touches the record.
                  Every proposal stays unconfirmed until a person accepts it, and no
                  clock moves on an unconfirmed link.
                </div>
              )}

              {data.may_confirm ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {selected.linked_event && selected.link_confirmation !== "confirmed" && (
                    <button className="primary" disabled={busy} onClick={() => decide("confirm")}>
                      Confirm the link to {selected.linked_event}
                    </button>
                  )}
                  {selected.link_confirmation !== "confirmed" && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <select value={target} onChange={(e) => setTarget(e.target.value)}
                        style={{ flex: 1, font: "inherit", padding: "8px 10px",
                                 borderRadius: 8, border: "1px solid var(--rule)" }}>
                        <option value="">Link it to…</option>
                        {data.events.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <button className="btn" disabled={busy || !target}
                        onClick={() => decide("confirm", target)}>Link</button>
                    </div>
                  )}
                  {selected.linked_event && selected.link_confirmation !== "rejected" && (
                    <button className="btn" disabled={busy} onClick={() => decide("reject")}>
                      {selected.link_confirmation === "confirmed" ? "Withdraw the link" : "Reject the proposal"}
                    </button>
                  )}
                </div>
              ) : (
                <p className="withheld" style={{ fontSize: 12 }}>
                  Confirming a link needs the Project Manager or Contractor role.
                </p>
              )}
              {problem && <div className="err">{problem}</div>}
            </section>
          )}
        </aside>
      </div>
    </>
  );
}
